import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  NumberInput,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Divider,
  Text,
  TextInput,
  Alert,
  Loader,
  Center,
  Switch,
  Modal,
  List,
  rem,
} from "@mantine/core";

// Descriptions vary in length across a row (some have none, "Driver payout %"
// wraps to two lines) — without a shared reserved height each NumberInput is
// top-anchored within its own grid cell, so the actual input boxes land at
// different Y positions instead of lining up.
const fieldStyles = { description: { minHeight: rem(36) } };
import { notifications } from "@mantine/notifications";
import { IconInfoCircle } from "@tabler/icons-react";
import { PageHeader } from "../../components/common/PageHeader";
import { CashbackSettingsCard } from "../../components/finance/CashbackSettingsCard";
import { QueryError } from "../../components/common/QueryError";
import { settingsApi } from "../../api/settings";
import { errorMessage } from "../../lib/axios";
import { formatMoney } from "../../lib/format";
import type {
  BookingConfig,
  DebtLimitDriver,
  DebtLimitImpact,
  SafetyConfig,
} from "../../types/domain";

const EMPTY: BookingConfig = {};
const EMPTY_SAFETY: SafetyConfig = {};

export function SettingsPage() {
  const qc = useQueryClient();
  const [v, setV] = useState<BookingConfig>(EMPTY);
  const [safety, setSafety] = useState<SafetyConfig>(EMPTY_SAFETY);
  // Set while the "sync affected drivers?" choice is open.
  const [debtImpact, setDebtImpact] = useState<DebtLimitImpact | null>(null);
  const [checkingImpact, setCheckingImpact] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["settings"],
    queryFn: settingsApi.get,
  });

  useEffect(() => {
    if (data?.bookingConfig) setV(data.bookingConfig);
    if (data?.safetyConfig) setSafety(data.safetyConfig);
  }, [data]);

  // Booking and safety config are separate endpoints; one Save button writes
  // both. Sequential rather than parallel so a safety failure cannot be
  // masked by a booking success.
  const saveMutation = useMutation({
    mutationFn: async (body: {
      booking: Partial<BookingConfig>;
      safety: Partial<SafetyConfig>;
      syncDebtLimits?: boolean;
    }) => {
      await settingsApi.updateBookingConfig(body.booking);
      // Send "" rather than undefined to clear the number — omitting the key
      // means "leave unchanged" on the backend.
      await settingsApi.updateSafetyConfig({
        ...body.safety,
        policeEmergencyNumber: body.safety.policeEmergencyNumber ?? "",
      });
      // After the save, so the sync reads the new ceiling.
      return body.syncDebtLimits ? settingsApi.syncDebtLimits() : null;
    },
    onSuccess: (sync) => {
      setDebtImpact(null);
      notifications.show({
        color: sync?.failed ? "yellow" : "green",
        message: sync
          ? `Settings saved. Drivers synced: ${sync.unblocked} can take trips, ${sync.blocked} blocked${
              sync.failed ? `, ${sync.failed} failed (re-run the sync)` : ""
            }.`
          : "Settings saved",
      });
      // Re-read: confirms the values actually persisted.
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  // A changed debt ceiling only reaches drivers already blocked (or now over
  // it) on their next balance change, so offer to re-check them right away.
  const save = async () => {
    const before = data?.bookingConfig?.maxDriverDebt;
    const after = v.maxDriverDebt;
    if (after === undefined || after === before) {
      saveMutation.mutate({ booking: v, safety });
      return;
    }
    setCheckingImpact(true);
    try {
      const impact = await settingsApi.debtLimitImpact(after);
      const affected =
        impact.toBlock.length +
        impact.toUnblock.length +
        impact.legacyLocked.length;
      if (affected === 0) saveMutation.mutate({ booking: v, safety });
      else setDebtImpact(impact);
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    } finally {
      setCheckingImpact(false);
    }
  };

  if (isLoading) {
    return (
      <Center h={240}>
        <Loader />
      </Center>
    );
  }

  if (error) return <QueryError error={error} onRetry={() => refetch()} />;

  const num = (key: Exclude<keyof BookingConfig, "partWalletEnabled">) => ({
    value: v[key] ?? "",
    onChange: (val: string | number) =>
      setV({ ...v, [key]: val === "" ? undefined : Number(val) }),
  });

  return (
    <Stack>
      <PageHeader
        title="Settings"
        subtitle="Platform-wide pricing, fees, driver limits and safety"
        actions={
          <Button
            loading={saveMutation.isPending || checkingImpact}
            onClick={save}
          >
            Save changes
          </Button>
        }
      />

      <Modal
        opened={debtImpact !== null}
        onClose={() => setDebtImpact(null)}
        title="Apply the new debt limit to drivers now?"
        size="lg"
      >
        {debtImpact && (
          <Stack>
            <Text size="sm">
              New limit: <b>{formatMoney(debtImpact.globalMax)}</b>. Drivers
              with their own limit keep it. Balances are not changed.
            </Text>
            <ImpactList
              title="Can take trips again"
              drivers={debtImpact.toUnblock}
            />
            <ImpactList
              title="Will be blocked from new trips (and notified)"
              drivers={debtImpact.toBlock}
            />
            <ImpactList
              title="Old account lock released (then re-checked)"
              drivers={debtImpact.legacyLocked}
            />
            <Text size="xs" c="dimmed">
              Save only: these drivers keep their current state until their
              balance next changes. A blocked driver who cannot take trips may
              wait a long time for that.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setDebtImpact(null)}>
                Cancel
              </Button>
              <Button
                variant="light"
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate({ booking: v, safety })}
              >
                Save only
              </Button>
              <Button
                loading={saveMutation.isPending}
                onClick={() =>
                  saveMutation.mutate({
                    booking: v,
                    safety,
                    syncDebtLimits: true,
                  })
                }
              >
                Save & sync drivers
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Card withBorder radius="md">
        <Stack>
          <Divider label="Commission & fees" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <NumberInput
              label="Smart Safiri commission %"
              description="Default rate. A ride type can override it."
              min={0}
              max={100}
              decimalScale={2}
              styles={fieldStyles}
              {...num("safiriCommissionPercentage")}
            />
            <NumberInput
              label="Booking fee %"
              description=" "
              min={0}
              max={100}
              decimalScale={2}
              styles={fieldStyles}
              {...num("bookingFeePercentage")}
            />
            <NumberInput
              label="VAT %"
              description="Tanzanian standard rate is 18%"
              min={0}
              max={100}
              decimalScale={2}
              styles={fieldStyles}
              {...num("vatPercentage")}
            />
          </SimpleGrid>

          <Divider label="Upfront pricing" labelPosition="left" />
          <Alert
            variant="light"
            color="blue"
            icon={<IconInfoCircle size={16} />}
          >
            The fare quoted at booking stays the final charge as long as the
            actual trip's recalculated fare stays within this band around it
            — small drift never moves the price shown to the rider or the
            earnings shown to the driver at accept time. Only once the actual
            fare moves outside the band (up OR down) is the recalculated fare
            charged instead.
          </Alert>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <NumberInput
              label="Fare increase threshold %"
              description="Actual fare above quote by more than this → charge actual"
              min={0}
              decimalScale={2}
              {...num("upfrontFareIncreaseThresholdPercentage")}
            />
            <NumberInput
              label="Fare decrease threshold %"
              description="Actual fare below quote by more than this → charge actual"
              min={0}
              max={100}
              decimalScale={2}
              {...num("upfrontFareDecreaseThresholdPercentage")}
            />
          </SimpleGrid>

          <Divider label="Payment gateway fees" labelPosition="left" />
          <Alert
            variant="light"
            color="blue"
            icon={<IconInfoCircle size={16} />}
          >
            All three are <strong>additive</strong> — charged on top of the base
            amount, so the payer pays more rather than Smart Safiri receiving less.
            Spending an existing wallet balance is never charged a gateway fee.
          </Alert>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <NumberInput
              label="Ride payment %"
              description="Passenger, paying for a ride online"
              min={0}
              max={100}
              decimalScale={2}
              styles={fieldStyles}
              {...num("ridePaymentGatewayFeePercentage")}
            />
            <NumberInput
              label="Wallet top-up %"
              description="Passenger, topping up their wallet"
              min={0}
              max={100}
              decimalScale={2}
              styles={fieldStyles}
              {...num("walletTopUpGatewayFeePercentage")}
            />
            <NumberInput
              label="Driver payout %"
              description="Driver, settling commission via the gateway. Manual/cash settlements are exempt."
              min={0}
              max={100}
              decimalScale={2}
              styles={fieldStyles}
              {...num("driverPayoutGatewayFeePercentage")}
            />
          </SimpleGrid>

          <Divider label="LATRA" labelPosition="left" />
          <Alert
            variant="light"
            color="yellow"
            icon={<IconInfoCircle size={16} />}
          >
            Charged to the <strong>rider</strong> on every trip as a
            percentage of the actual fare, like the booking fee. Smart Safiri
            passes it to LATRA: it is not Smart Safiri revenue and nothing is
            taken from the driver. Monthly totals are in LATRA → Monthly report.
          </Alert>
          <NumberInput
            label="LATRA levy %"
            description="Of the actual fare, paid by the rider"
            min={0}
            max={100}
            decimalScale={2}
            maw={{ base: "100%", sm: 300 }}
            {...num("latraLevyPercentage")}
          />

          <Divider label="Driver limits" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <NumberInput
              label="Max driver debt (TZS)"
              description="Default ceiling. Can be overridden per driver."
              min={0}
              thousandSeparator=","
              styles={fieldStyles}
              {...num("maxDriverDebt")}
            />
            <NumberInput
              label="Free waiting minutes"
              description=" "
              min={0}
              max={60}
              styles={fieldStyles}
              {...num("freeWaitingMinute")}
            />
            <NumberInput
              label="Pickup arrival radius (m)"
              description="How close the driver must be to mark arrived."
              placeholder="1000"
              min={50}
              max={5000}
              thousandSeparator=","
              styles={fieldStyles}
              {...num("pickupArrivalRadiusMeters")}
            />
            <NumberInput
              label="Stop arrival radius (m)"
              description="How close the driver must be to mark a stop reached."
              placeholder="300"
              min={50}
              max={5000}
              thousandSeparator=","
              styles={fieldStyles}
              {...num("stopArrivalRadiusMeters")}
            />
            <NumberInput
              label="Minimum cash-out (TZS)"
              description="Balance a driver must reach before they can withdraw."
              min={0}
              thousandSeparator=","
              {...num("minimumPayoutThresholdAmount")}
            />
          </SimpleGrid>

          <Text size="xs" c="dimmed">
            A driver who crosses the debt ceiling mid-trip is allowed to finish
            that trip; the lock only stops them being offered a new one.
          </Text>

          <Text size="xs" c="dimmed">
            The cash-out minimum applies to the other direction — what Smart Safiri
            owes a driver. Below it, no withdraw action is offered in the driver
            app. Raising it strands balances that were previously withdrawable.
            Smart Safiri absorbs the disbursement fee, so the driver always receives
            their full balance; the &ldquo;Driver payout %&rdquo; above is a
            collection-side charge and does not apply here.
          </Text>

          <Divider label="Wallet + cash payments" labelPosition="left" />
          <Switch
            label="Let riders pay part from wallet, rest in cash"
            description="When a Wallet rider's balance is short at the destination, take what the wallet holds and have the driver collect only the rest in cash. Turning it off goes back to switching the whole fare to cash."
            checked={v.partWalletEnabled ?? false}
            onChange={(e) => setV({ ...v, partWalletEnabled: e.currentTarget.checked })}
          />
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <NumberInput
              label="Cash rounding step (TZS)"
              description="The cash due is always a multiple of this."
              placeholder="500"
              min={100}
              max={10000}
              step={100}
              thousandSeparator=","
              styles={fieldStyles}
              {...num("partWalletCashStep")}
            />
            <NumberInput
              label="Unconfirmed cash review (hours)"
              description="After this, the trip shows in the inbox."
              placeholder="24"
              min={1}
              max={720}
              styles={fieldStyles}
              {...num("partWalletReviewHours")}
            />
          </SimpleGrid>
          <Alert variant="light" color="yellow" icon={<IconInfoCircle />}>
            Only switch this on once the rider and driver apps that show the
            wallet / cash split are a mandatory update. An older driver app asks
            for the full fare in cash after the wallet part was already taken,
            so the rider would pay twice. The same switch also moves cash
            drivers to owing Smart Safiri&apos;s share (fare minus their
            earning) instead of commission + booking fee.
          </Alert>

          <Divider label="Safety & verification" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <TextInput
              label="Police emergency number"
              description="Alerted on every SOS, alongside personal contacts"
              placeholder="+255112345678"
              value={safety.policeEmergencyNumber ?? ""}
              onChange={(e) =>
                setSafety({
                  ...safety,
                  policeEmergencyNumber: e.currentTarget.value,
                })
              }
            />
            <NumberInput
              label="Selfie re-verification (days)"
              description="How long a driver's identity check stays valid"
              min={1}
              max={365}
              value={safety.driverSelfieVerificationDays ?? ""}
              onChange={(val) =>
                setSafety({
                  ...safety,
                  driverSelfieVerificationDays:
                    val === "" ? undefined : Number(val),
                })
              }
            />
          </SimpleGrid>

          <Text size="xs" c="dimmed">
            Without a police number, an SOS raised on a trip where neither party
            has saved an emergency contact reaches nobody at all.
          </Text>

          <Group justify="flex-end">
            <Button
              loading={saveMutation.isPending || checkingImpact}
              onClick={save}
            >
              Save changes
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Same card as Finance → Cashback → Settings, so the programme can be
          found from either place. It loads and saves on its own. */}
      <Divider label="Ride cashback" labelPosition="left" mt="md" />
      <Text size="xs" c="dimmed" mt={-8}>
        Saved separately with its own button. Also under Finance → Cashback → Settings.
      </Text>
      <CashbackSettingsCard />
    </Stack>
  );
}

const IMPACT_PREVIEW = 8;

function ImpactList({
  title,
  drivers,
}: {
  title: string;
  drivers: DebtLimitDriver[];
}) {
  if (drivers.length === 0) return null;
  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>
        {title}: {drivers.length}
      </Text>
      <List size="sm" spacing={2}>
        {drivers.slice(0, IMPACT_PREVIEW).map((d) => (
          <List.Item key={d.driverId}>
            {d.name} · owes {formatMoney(d.platformDebt)} / limit{" "}
            {formatMoney(d.maxDebt)}
          </List.Item>
        ))}
      </List>
      {drivers.length > IMPACT_PREVIEW && (
        <Text size="xs" c="dimmed">
          and {drivers.length - IMPACT_PREVIEW} more
        </Text>
      )}
    </Stack>
  );
}
